import {
  IdeationAssembledContextSchema,
  IdeationMapNodeSchema,
  NodeDocumentViewSchema,
  CurrentPositionResultSchema,
  ClaimImpactSchema,
  EpistemicClaimRecordSchema,
  SubjectContextBundleSchema,
  type ClaimImpact,
  type CurrentPositionResult,
  type EpistemicClaimCreate,
  type EpistemicClaimRecord,
  type IdeationAssembledContext,
  type IdeationMapNode,
  type NodeDocumentView,
  type SubjectContextBundle,
  SubjectPromoteAndAttachResultSchema,
  type SubjectPromoteAndAttach,
  type SubjectPromoteAndAttachResult,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { staffTenantCacheScope } from "../lib/staff-tenant-cache.js";
import { retrySubjectQuery } from "../lib/staff-tenant-switch.js";

import { apiFetch, apiFetchUnchecked } from "./client.js";

export const SUBJECT_KEY = ["subject-workspace"] as const;

export function subjectWorkspaceQueryKey(
  tenantScope: string,
  ...parts: Array<string | number | null | undefined>
) {
  return [...SUBJECT_KEY, tenantScope, ...parts] as const;
}

function subjectKey(...parts: Array<string | number | null | undefined>) {
  return subjectWorkspaceQueryKey(staffTenantCacheScope(), ...parts);
}

export function useSubjectNode(subjectId: string | null, enabled = true) {
  return useQuery<IdeationMapNode>({
    queryKey: subjectKey("node", subjectId),
    enabled: enabled && Boolean(subjectId),
    retry: retrySubjectQuery,
    queryFn: () => apiFetch(`/ideation-maps/nodes/${subjectId}`, {}, IdeationMapNodeSchema),
  });
}

export function useSubjectAssembledContext(subjectId: string | null, depth = 1, enabled = true) {
  return useQuery<IdeationAssembledContext>({
    queryKey: subjectKey("context", subjectId, depth),
    enabled: enabled && Boolean(subjectId),
    retry: retrySubjectQuery,
    queryFn: () =>
      apiFetch(
        `/ideation-maps/nodes/${subjectId}/context?depth=${depth}`,
        {},
        IdeationAssembledContextSchema,
      ),
  });
}

export function useSubjectNodeDocuments(subjectId: string | null, enabled = true) {
  return useQuery<NodeDocumentView[]>({
    queryKey: subjectKey("documents", subjectId),
    enabled: enabled && Boolean(subjectId),
    retry: retrySubjectQuery,
    queryFn: () =>
      apiFetch(`/ideation-maps/nodes/${subjectId}/documents`, {}, NodeDocumentViewSchema.array()),
  });
}

export function useSubjectContextBundle(subjectId: string | null, enabled = true) {
  return useQuery<SubjectContextBundle>({
    queryKey: subjectKey("context-bundle", subjectId),
    enabled: enabled && Boolean(subjectId),
    retry: retrySubjectQuery,
    queryFn: () =>
      apiFetch(`/subjects/${subjectId}/context-bundle`, {}, SubjectContextBundleSchema),
  });
}

export function useClaimImpact(claimId: string | null, enabled = true) {
  return useQuery<ClaimImpact>({
    queryKey: subjectKey("claim-impact", claimId),
    enabled: enabled && Boolean(claimId),
    retry: retrySubjectQuery,
    queryFn: () => apiFetch(`/subjects/claims/${claimId}/impact`, {}, ClaimImpactSchema),
  });
}

export function useSubjectCurrentPosition(subjectId: string | null, enabled = true) {
  return useQuery<CurrentPositionResult>({
    queryKey: subjectKey("current-position", subjectId),
    enabled: enabled && Boolean(subjectId),
    staleTime: 60_000,
    retry: retrySubjectQuery,
    queryFn: () =>
      apiFetch(`/subjects/${subjectId}/current-position`, {}, CurrentPositionResultSchema),
  });
}

export async function refreshSubjectCurrentPosition(
  subjectId: string,
): Promise<CurrentPositionResult> {
  return apiFetch(
    `/subjects/${subjectId}/current-position?refresh=1`,
    {},
    CurrentPositionResultSchema,
  );
}

function invalidateSubjectEvidence(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [...SUBJECT_KEY] });
}

/** Reuses POST /ideation-maps/nodes/:id/documents. */
export function useAttachSubjectEvidenceDocument(subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiFetchUnchecked<{ ok: true }>(`/ideation-maps/nodes/${subjectId}/documents`, {
        method: "POST",
        body: JSON.stringify({ documentId }),
      }),
    onSuccess: () => invalidateSubjectEvidence(qc),
  });
}

export function useCreateSubjectClaim(subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EpistemicClaimCreate) =>
      apiFetch<EpistemicClaimRecord>(
        `/subjects/${subjectId}/claims`,
        { method: "POST", body: JSON.stringify(body) },
        EpistemicClaimRecordSchema,
      ),
    onSuccess: () => invalidateSubjectEvidence(qc),
  });
}

export function usePromoteAndAttachWorkingDocument(subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubjectPromoteAndAttach) =>
      apiFetch<SubjectPromoteAndAttachResult>(
        `/subjects/${subjectId}/evidence/promote-and-attach`,
        { method: "POST", body: JSON.stringify(body) },
        SubjectPromoteAndAttachResultSchema,
      ),
    onSuccess: (result) => {
      if (result.attached) invalidateSubjectEvidence(qc);
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
      void qc.invalidateQueries({ queryKey: ["vault-documents"] });
    },
  });
}
